import { useMediaQuery } from '@material-ui/core'
import { useEffect, useState } from 'react'
import Confetti from 'react-confetti'
import { useTranslation } from 'react-i18next'
import Joyride, {
	ACTIONS,
	type CallBackProps,
	EVENTS,
	STATUS,
	type Step,
} from 'react-joyride'
import WalkthroughTooltip from 'src/components/ui/Walkthrough/WalkthroughTooltip'
import { useFetchUserInfoQuery } from 'src/redux/api/graphql/userInfoApi'
import {
	setIsChooseCompanyMenuOpen,
	setIsUserMenuOpen,
	updateUserInfo,
} from 'src/redux/slices/settings'
import {
	useAppDispatch,
	useAppSelector,
	useAppThunkDispatch,
} from 'src/utils/hooks/reduxHooks'

const FreelancerWalkthrough = () => {
	const dispatch = useAppDispatch()
	const dispatchThunk = useAppThunkDispatch()
	const { t, i18n } = useTranslation()
	const { t: tSettings } = useTranslation('settings')
	const language = i18n.language
	const isSmall = useMediaQuery('(max-width: 1090px)')
	const userInfoId = useAppSelector((state) => state.auth.userInfo?.id)
	const { data: userInfo } = useFetchUserInfoQuery(
		{
			userInfoId: userInfoId!,
		},
		{
			skip: !userInfoId,
		}
	)
	const userInformation = useAppSelector(
		(state) => state.settings.userInformation
	)
	console.log('userInformation: ', userInformation)
	//! Change to our boolean
	const hideTour = userInformation?.hideTour
	const isFreelancer = userInfo?.cognitoGroup?.includes('freelancer')
	const isUserMenuOpen = useAppSelector(
		(state) => state.settings.isUserMenuOpen
	)
	const isChooseCompanyMenuOpen = useAppSelector(
		(state) => state.settings.isChooseCompanyMenuOpen
	)
	const fetchProjectChildrenApiStatus = useAppSelector(
		(state) => state.projects.fetchProjectChildrenApiStatus
	)
	const [showConfetti, setShowConfetti] = useState(false)
	const [dontShowTour, setDontShowTour] = useState(false)
	const handleChange = () => {
		setDontShowTour(!dontShowTour)
	}
	const newSteps: Step[] = [
		{
			content: (
				<>
					<p>{tSettings('walkthrough.startingSlide')}</p>
				</>
			),
			placement: 'center',
			//disableBeacon: true,
			target: 'body',
		},
		{
			content: (
				<>
					<p>{tSettings('walkthrough.listOfCompaniesMainSlide')}</p>
				</>
			),
			placement: 'auto',
			//disableBeacon: true,
			target: '.listOfCompaniesIcon',
		},
		{
			content: (
				<>
					<p>
						{tSettings('walkthrough.listOfCompaniesContentSlide')}
					</p>
				</>
			),
			placement: 'left',
			//disableBeacon: true,
			offset: 50,
			spotlightPadding: 20,
			target: '.listOfCompaniesContent',
		},
		{
			content: (
				<>
					<p>{tSettings('walkthrough.accountSettingsMainSlide')}</p>
				</>
			),
			placement: 'auto',
			//disableBeacon: true,
			target: '.settingsButtonIcon',
		},
		{
			content: (
				<>
					<p>
						{tSettings('walkthrough.accountSettingsContentSlide')}
					</p>
				</>
			),
			placement: 'left',
			//disableBeacon: true,
			target: '.settingsButtonContent',
		},
		{
			content: (
				<>
					<p>{tSettings('walkthrough.quoteButtonSlide')}</p>
				</>
			),
			placement: 'left',
			//disableBeacon: true,
			target: '.createQuoteButton',
		},
		{
			content: (
				<>
					<p>{tSettings('walkthrough.projectButtonSlide')}</p>
				</>
			),
			placement: 'left',
			//disableBeacon: true,
			target: '.createProjectButton',
		},
		{
			content: (
				<>
					<p>{tSettings('walkthrough.finalSlide')}</p>
				</>
			),
			placement: 'center',
			//disableBeacon: true,
			target: 'body',
		},
	]

	interface TourState {
		run: boolean
		steps: Step[]
		stepIndex: number
		isUserMenuOpen: boolean
		isChooseCompanyMenuOpen: boolean
	}

	const [tourState, setTourState] = useState<TourState>({
		run: true,
		stepIndex: 0,
		steps: newSteps,
		isUserMenuOpen: false,
		isChooseCompanyMenuOpen: false,
	})
	// biome-ignore lint: Language initialization - sets browser language for walkthrough on mount
	useEffect(() => {
		// Getting and setting browser language, need to do it here since we set up langugage in auth redux(check autentication status) to the user profile language
		const userLang = navigator.language
		i18n.changeLanguage(userLang ?? 'fr')
	}, [])
	useEffect(() => {
		setTourState({ ...tourState, steps: newSteps })
	}, [dontShowTour])
	useEffect(() => {
		setTourState({ ...tourState, steps: newSteps })
	}, [language])

	// biome-ignore lint: Legitimate effect responding to menu state changes
	useEffect(() => {
		console.log('isUserMenuOpen: ', isUserMenuOpen)
		if (isUserMenuOpen) {
			setTourState({ ...tourState, run: true, stepIndex: 4 })
		}
	}, [isUserMenuOpen])

	// biome-ignore lint: Legitimate effect responding to menu state changes
	useEffect(() => {
		console.log('isChooseCompanyMenuOpen: ', isChooseCompanyMenuOpen)
		if (isChooseCompanyMenuOpen) {
			setTourState({ ...tourState, run: true, stepIndex: 2 })
		}
	}, [isChooseCompanyMenuOpen])

	const handleJoyrideCallback = (data: CallBackProps) => {
		const { status, type, step, index, action } = data
		const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED]

		if (finishedStatuses.includes(status)) {
			setTourState({ ...tourState, run: false, stepIndex: 0 })
			// Close menu on skip
			dispatch(setIsUserMenuOpen(false))
			dispatch(setIsChooseCompanyMenuOpen(false))
			if (status === STATUS.FINISHED) {
				// To show confetti after the tour
				// setShowConfetti(true)
				// If confetti is to be shown on last slide
				setShowConfetti(false)
			}
			if (dontShowTour) {
				//! Change to our boolean
				dispatchThunk(updateUserInfo({ hideTour: true }))
			}
		} else if (
			([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND] as string[]).includes(
				type
			)
		) {
			const nextStepIndex = index + (action === ACTIONS.PREV ? -1 : 1)
			console.log('action: ', action)
			console.log('index: ', index)
			console.log('hereee')
			console.log('step: ', step)
			// If company modal is closed and we want to move open from next or prev
			if (
				(index === 1 && action === ACTIONS.NEXT) ||
				(index === 3 && action === ACTIONS.PREV)
			) {
				dispatch(setIsChooseCompanyMenuOpen(true))
			}
			// if company modal is open and we move
			if (index === 2) {
				dispatch(setIsChooseCompanyMenuOpen(false))
			}
			// if user modal is open and we move
			if (index === 4) {
				dispatch(setIsUserMenuOpen(false))
			}
			// If user modal is closed and we want to move open from next or prev
			if (index === 5 && action === ACTIONS.PREV) {
				dispatch(setIsUserMenuOpen(true))
			}
			if (index === 3) {
				if (action === ACTIONS.PREV) {
					dispatch(setIsUserMenuOpen(false))
				} else {
					dispatch(setIsUserMenuOpen(true))
				}
			}
			// If confetti is to be shown on last slide
			if (index === 6 && action === ACTIONS.NEXT) {
				console.log('index: ', index)
				setShowConfetti(true)
			}
			if (index === 7 && action === ACTIONS.PREV) {
				console.log('index: ', index)
				setShowConfetti(false)
			}

			// if (isUserMenuOpen) {
			//   setTourState({ ...tourState, stepIndex: nextStepIndex })
			//   console.log('isUserMenuOpen: ', isUserMenuOpen)
			// }

			setTourState({ ...tourState, stepIndex: nextStepIndex })
		}
	}
	return (
		<div>
			{fetchProjectChildrenApiStatus === 'succeeded' &&
				isFreelancer &&
				!hideTour &&
				!isSmall && (
					<Joyride
						callback={handleJoyrideCallback}
						continuous
						hideCloseButton
						run={tourState.run}
						// Enable to  make elements clickable
						// spotlightClicks={true}
						scrollToFirstStep
						showProgress
						showSkipButton
						steps={tourState.steps}
						stepIndex={tourState.stepIndex}
						tooltipComponent={(props) => (
							<WalkthroughTooltip
								props={props}
								dontShowTour={dontShowTour}
								handleChange={handleChange}
								disableNextStepsArray={[]}
							/>
						)}
						styles={{
							options: {
								zIndex: 10000,
							},
						}}
						disableOverlayClose
						disableCloseOnEsc
					/>
				)}
			{showConfetti && (
				<Confetti
					numberOfPieces={200}
					// onConfettiComplete={(confetti: any) => {
					//   console.log('a:', confetti)
					//   return setShowConfetti(false)
					// }}
					tweenDuration={10000}
					style={{ zIndex: 10000 }}
					// recycle={false}
				/>
			)}
		</div>
	)
}

export default FreelancerWalkthrough
